package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Caption
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import dev.lingvo.site.i18n.t

enum class LTableHeaderScope(val htmlValue: String) {
    COLUMN("col"),
    ROW("row"),
}

/** Which edge a cell's content sits against. Numeric columns read down their last digit. */
enum class LTableAlign { START, END }

/**
 * How much of a table the reader has selected, as the header's checkbox has to express it.
 *
 * Three states, not a `Boolean`, because the middle one is the one that matters: a header tick over
 * a table where four of two hundred rows are selected says "everything is selected", and the bulk
 * action taken on the strength of it is the one nobody can undo. [SOME] renders as an indeterminate
 * checkbox — see `LCheckbox(indeterminate = …)`.
 */
enum class LTableSelection { NONE, SOME, ALL }

val LTableContainerStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .overflow { x(Overflow.Auto) }
            .backgroundColor(ColorTokens.Surface.value())
            .border(1.px, LineStyle.Solid, ColorTokens.Border.value())
            .borderRadius(Radius.Card)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, ColorTokens.Accent.value())
            .styleModifier { property("outline-offset", "2px") }
    }
}

val LTableStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(40.cssRem)
        .color(ColorTokens.TextPrimary.value())
        .fontSize(TypeScale.BodySmall)
        .styleModifier {
            property("border-collapse", "collapse")
            property("border-spacing", "0")
        }
}

val LTableCaptionStyle = CssStyle.base {
    Modifier
        .color(ColorTokens.TextSecondary.value())
        .padding(topBottom = Space.MD, leftRight = Space.LG)
        .fontSize(TypeScale.Small)
        .fontWeight(500)
        .styleModifier {
            property("caption-side", "top")
            property("text-align", "start")
        }
}

val LTableHeadStyle = CssStyle.base {
    Modifier.backgroundColor(ColorTokens.SurfaceSunken.value())
}

/**
 * Zebra striping and hover, kept distinguishable from each other and from the container.
 *
 * Hover used to paint `surfaceRaised` over a container filled with `surface` — **1.03:1 in Day**,
 * computed from the shipped hexes, on the only affordance 26 data tables carry. `accentSubtle` is
 * the fix, and it is a real affordance rather than a fill: `surface`/`accentSubtle` measures 1.23:1
 * in Day and 1.09:1 in Night (`PaletteContrastTest`). Night's number is close to the 1.11:1 the
 * broken `surfaceRaised` hover measured there — the luminance-only formula cannot see it, but
 * `accentSubtle` in Night is a cool indigo against a warm neutral ground, a hue shift a reader
 * notices that a pure-luminance ratio does not credit.
 *
 * Hover and selection share that same `accentSubtle` fill, which made a hovered unselected row
 * render identically to a selected one. Selection gets a second, non-colour carrier instead of a
 * different fill — this codebase's own pattern for "a hue-only signal disappears in greyscale":
 * `AppShell.kt`'s `[aria-current="page"]::before` bar and `SectionNav.kt`'s `borderStrong` stroke
 * both exist for the identical reason. Here it is a 3px accent bar inset on the row's leading edge
 * (`box-shadow: inset`, not a real border or an absolutely-positioned pseudo-element on a `<tr>`,
 * neither of which reliably participates in table layout). Hover keeps the fill alone — the bar is
 * what separates "selected" from "hovered", so hover must never gain one.
 *
 * Source order is load-bearing. `:nth-child(even)`, `:hover` and `[aria-selected="true"]` are all
 * one selector component — equal CSS specificity — so ties go to declaration order, not to which
 * rule "sounds" more specific:
 *  - `:nth-child(even)` is declared first, so an unselected, hovered, even row still shows hover
 *    rather than the stripe underneath it — the whole reason for finding this ordering in the first
 *    place.
 *  - `[aria-selected="true"]` is declared after it, so a selected even row shows selection rather
 *    than the stripe, and — at the same equal specificity — after `hover` too, which is *already*
 *    enough to win a hovered-selected row's background without the rule below.
 *  - `[aria-selected="true"]:hover` looks redundant for exactly that reason, and it is redundant
 *    for the *current* source order. It stays anyway, because it is not order-dependent — two
 *    components beats one regardless of where either block sits in the file — while the other two
 *    rules above are. Reorder `hover {}` to come after `[aria-selected="true"]` some day (a plausible
 *    edit; nothing here signals not to) and this rule is the one thing that keeps a hovered selected
 *    row's fill from silently flipping to plain hover's. It does not redeclare the leading-edge bar:
 *    `box-shadow` cascades per-property, not per-rule, so the plain `[aria-selected="true"]` rule's
 *    bar keeps applying to a hovered selected row untouched regardless.
 *
 * Selection's fill and bar both carry `.noTransition()`; hover's fill does not. Hover is a
 * continuous pointer signal — it fades so a moving cursor doesn't flicker the stripe underneath.
 * Selection is a discrete action (a click, a range-select) and reads as confirmation of that action,
 * so it snaps rather than catching up a beat late.
 */
val LTableRowStyle = CssStyle {
    base {
        Modifier
            .styleModifier { property("background-color", "transparent") }
            // The helper, not a hand-written string, so a change to `Motion.ColorProperties`
            // reaches the zebra hover instead of stopping one file short of it.
            .transitionColors()
    }
    cssRule(":nth-child(even)") {
        Modifier.backgroundColor(ColorTokens.SurfaceSunken.value())
    }
    hover {
        Modifier.backgroundColor(ColorTokens.AccentSubtle.value())
    }
    cssRule("[aria-selected=\"true\"]") {
        Modifier
            .backgroundColor(ColorTokens.AccentSubtle.value())
            .noTransition()
            .styleModifier {
                property("box-shadow", "inset 3px 0 0 0 ${ColorTokens.Accent.value()}")
            }
    }
    cssRule("[aria-selected=\"true\"]:hover") {
        Modifier.backgroundColor(ColorTokens.AccentSubtle.value())
    }
}

val LTableHeaderCellStyle = CssStyle.base {
    Modifier
        .color(ColorTokens.TextSecondary.value())
        .padding(topBottom = Space.MD, leftRight = Space.LG)
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .styleModifier {
            property("border-bottom", "1px solid ${ColorTokens.BorderStrong.value()}")
            property("letter-spacing", TypeScale.MicroTracking.toString())
            // `start`, not `left`. The cell beneath has no `text-align` at all, so it resolves to
            // `start` and follows the writing direction; a header pinned to `left` would sit on the
            // opposite side from its own column the moment this interface ships in Arabic or
            // Hebrew — which, on a localization product, is not a hypothetical market.
            property("text-align", "start")
            property("text-transform", "uppercase")
            property("vertical-align", "middle")
            property("white-space", "nowrap")
        }
}

/**
 * A `th` that heads its *row*, which is a data value and has to be painted as one.
 *
 * [LTableHeaderCellStyle] is column chrome: uppercase, tracked out, `Micro`, secondary, over the
 * heavier `borderStrong` rule that separates the head from the body. Applied to a row header — a key
 * name, a file path, a member's email — it mangles the content it is supposed to carry
 * (`composeResources/values/strings.xml` became `COMPOSERESOURCES/VALUES/STRINGS.XML`, and
 * `checkout.pay_now` became `CHECKOUT.PAY_NOW`, on a product whose keys are case-sensitive
 * identifiers), and draws the head's divider under one column of every body row, which reads as a
 * broken rule. `scope` is a semantic statement about the markup; it was never a request for a
 * different typeface. So a row header is a body cell that happens to name its row.
 */
val LTableRowHeaderCellStyle = CssStyle.base {
    Modifier
        .color(ColorTokens.TextPrimary.value())
        .padding(topBottom = Space.MD, leftRight = Space.LG)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(500)
        .styleModifier {
            property("border-bottom", "1px solid ${ColorTokens.Border.value()}")
            property("text-align", "start")
            property("vertical-align", "middle")
        }
}

val LTableCellStyle = CssStyle.base {
    Modifier
        .color(ColorTokens.TextPrimary.value())
        .padding(topBottom = Space.MD, leftRight = Space.LG)
        .styleModifier {
            property("border-bottom", "1px solid ${ColorTokens.Border.value()}")
            property("vertical-align", "middle")
        }
}

@Composable
fun LTable(
    modifier: Modifier = Modifier,
    tableModifier: Modifier = Modifier,
    caption: String? = null,
    /**
     * Keep the caption for assistive technology and stop painting it.
     *
     * For the page that already states the count in the control that decided it — a filter bar's
     * result summary — where a visible caption repeats the same number in different words a few
     * pixels below. The caption is still the table's name in the accessibility tree and still the
     * region's `aria-label`, so hiding it costs a screen reader nothing.
     */
    captionHidden: Boolean = false,
    content: @Composable () -> Unit,
) {
    // Hoisted out of `toAttrs`. The fallback is a visible, translatable string and `toAttrs` runs
    // outside composition, where `t` cannot: written inline it stayed English in every locale and
    // the extractor never saw it at all.
    val regionLabel = caption ?: t("ds.ds.ltable.data_table", "Data table")
    Div(
        LTableContainerStyle.toModifier()
            .then(modifier)
            .toAttrs {
                attr("role", "region")
                attr("aria-label", regionLabel)
                tabIndex(0)
            },
    ) {
        Table(LTableStyle.toModifier().then(tableModifier).toAttrs()) {
            if (caption != null) {
                Caption(
                    (if (captionHidden) LVisuallyHiddenStyle else LTableCaptionStyle).toModifier().toAttrs(),
                ) {
                    Text(caption)
                }
            }
            content()
        }
    }
}

@Composable
fun LTableHead(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Thead(LTableHeadStyle.toModifier().then(modifier).toAttrs()) {
        content()
    }
}

@Composable
fun LTableBody(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Tbody(modifier.toAttrs()) {
        content()
    }
}

/**
 * The narrow leading column a selectable table grows, on the header cell and on every row.
 *
 * `width: 1%` with `white-space: nowrap` is the table-layout idiom for "as narrow as its content":
 * a percentage under the intrinsic width of the checkbox is clamped up to it, and every other
 * column keeps the space this one does not take. A fixed pixel width would have to be guessed and
 * would be wrong at the first zoom level that is not 100%.
 */
val LTableSelectionCellStyle = CssStyle.base {
    Modifier
        .width(1.percent)
        .styleModifier {
            property("white-space", "nowrap")
            property("padding-inline-end", "0")
        }
}

/**
 * A row, optionally with a checkbox of its own in a leading cell.
 *
 * **`selectable` and `selected` are different questions**, and `aria-selected` has to answer both.
 * A selectable row that is not selected announces `aria-selected="false"`, which is what tells a
 * screen reader the row *can* be selected; the attribute is left off entirely on a table with no
 * selection at all, because `false` on every row of a plain data table announces a capability the
 * table does not have.
 *
 * [selectionLabel] names the row, not the column: "Select checkout.confirm", not "Select". The
 * column header already says what the control is, and a hundred rows that all announce "Select"
 * give a reader listening to them nothing to choose between — the label is hidden visually and is
 * the only thing distinguishing one row's checkbox from the next.
 */
@Composable
fun LTableRow(
    modifier: Modifier = Modifier,
    selected: Boolean = false,
    selectable: Boolean = false,
    onSelectedChange: ((Boolean) -> Unit)? = null,
    selectionLabel: String = t("ds.ds.ltable.select_this_row", "Select this row"),
    content: @Composable () -> Unit,
) {
    Tr(
        LTableRowStyle.toModifier()
            .then(modifier)
            .toAttrs {
                if (selectable || selected) attr("aria-selected", selected.toString())
            },
    ) {
        if (selectable) {
            LTableCell(LTableSelectionCellStyle.toModifier()) {
                LCheckbox(
                    checked = selected,
                    onCheckedChange = { onSelectedChange?.invoke(it) },
                    label = selectionLabel,
                    enabled = onSelectedChange != null,
                    labelHidden = true,
                )
            }
        }
        content()
    }
}

/**
 * The `th` that selects and clears the whole visible set.
 *
 * Its own accessible name is [label] — "Select every key on this page", not "Select all" — because
 * "all" is the word this control is most likely to be lying about. A table showing 200 of 4,000
 * rows selects 200, and the honest sentence about the remaining 3,800 belongs on the bulk bar's
 * `note`, where `LBulkActionBar` renders it.
 *
 * [onChange] receives `true` when the reader is asking for everything and `false` when they are
 * clearing: [LTableSelection.SOME] is treated as "not yet everything", so one press from a partial
 * selection completes it rather than throwing the reader's work away.
 */
@Composable
fun LTableSelectAllHeaderCell(
    state: LTableSelection,
    onChange: (Boolean) -> Unit,
    label: String = t(
        "ds.ds.ltable.select_every_row_on_this_page",
        "Select every row on this page",
    ),
    modifier: Modifier = Modifier,
) {
    Th(
        LTableHeaderCellStyle.toModifier()
            .then(LTableSelectionCellStyle.toModifier())
            .then(modifier)
            .toAttrs {
                attr("scope", LTableHeaderScope.COLUMN.htmlValue)
                attr("data-select-all", state.name.lowercase())
            },
    ) {
        LCheckbox(
            checked = state == LTableSelection.ALL,
            onCheckedChange = { onChange(state != LTableSelection.ALL) },
            label = label,
            labelHidden = true,
            indeterminate = state == LTableSelection.SOME,
        )
    }
}

/**
 * A `th`, which needs to say *what* it heads.
 *
 * **Inside [LTableBody], pass `scope = LTableHeaderScope.ROW`.** The default is `COLUMN` because
 * that is the common case in [LTableHead], and a `th` left on the default inside a body row tells a
 * screen reader it heads the whole column beneath it — so every later row's cells are announced
 * against that one row's label. Nine tables in this product had it, and the symptom is invisible
 * without a screen reader: the markup renders identically either way.
 *
 * `LTableRowHeaderScopeTest` pins one of them, and the default stays `COLUMN` rather than becoming
 * required because five header rows would then have to say the obvious thing.
 */
@Composable
fun LTableHeaderCell(
    modifier: Modifier = Modifier,
    scope: LTableHeaderScope = LTableHeaderScope.COLUMN,
    align: LTableAlign = LTableAlign.START,
    content: @Composable () -> Unit,
) {
    Th(
        (if (scope == LTableHeaderScope.ROW) LTableRowHeaderCellStyle else LTableHeaderCellStyle)
            .toModifier()
            .then(modifier)
            .then(if (align == LTableAlign.END) Modifier.textAlign(TextAlign.End).tabularNumbers() else Modifier)
            .toAttrs {
                attr("scope", scope.htmlValue)
            },
    ) {
        content()
    }
}

@Composable
fun LTableCell(
    modifier: Modifier = Modifier,
    align: LTableAlign = LTableAlign.START,
    content: @Composable () -> Unit,
) {
    Td(
        LTableCellStyle.toModifier()
            .then(modifier)
            .then(if (align == LTableAlign.END) Modifier.textAlign(TextAlign.End).tabularNumbers() else Modifier)
            .toAttrs(),
    ) {
        content()
    }
}
